import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import {
  studentAllFeeTransactions,
  studentFeeTransactionAccept,
} from "../../api/Student";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { BankList } from "../../api/BankService";
import { Link } from "react-router-dom";
import { formatToDate } from "../master/FormatToDateTime";
import usePrivateLink from "../config/usePrivateLink";
import { imgBaseUrl } from "../../api/api";

export default function AllFeesTransactions() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [filters, setFilters] = useState({});
  const [data, setData] = useState({});

  // Selected image for popup
  const [selectedImage, setSelectedImage] = useState(null);

  const getBusinessBanks = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getStudentFeeTransactions = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      Object.keys(filters).forEach((key) => {
        const value = filters[key];

        if (value) {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      const response = await studentAllFeeTransactions(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getAcceptPayload = (item) => {
    const payload = {
      id: item?.id,
      student_id: null,
      hostel_student_id: null,
      library_student_id: null,
      bus_student_id: null,
    };

    if (item?.hostel_student_id) {
      payload.hostel_student_id = item.hostel_student_id;
    } else if (item?.library_student_id) {
      payload.library_student_id = item.library_student_id;
    } else if (item?.bus_student_id) {
      payload.bus_student_id = item.bus_student_id;
    } else {
      payload.student_id = item?.student_id || null;
    }

    return payload;
  };

  const handleAccept = async (payload) => {
    const confirm = await Swal.fire({
      title: "Accept this transaction?",
      text: "This will mark the payment as accepted and apply it to the student's fees. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, accept it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await studentFeeTransactionAccept(payload);

      if (response?.data?.status) {
        SweetAlertShow("success", "Success", response?.data?.message);
        getStudentFeeTransactions();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusinessBanks();
  }, []);

  useEffect(() => {
    getStudentFeeTransactions();
  }, [filters]);

  const inr = (n) =>
    "₹ " +
    Number(n || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });

  const MODULE_BADGE_COLOR = {
    "Hostel Fee": "success",
    "Transport Fee": "warning",
    "Library Fee": "info",
  };

  const parseModule = (moduleStr) => {
    try {
      const parsed = JSON.parse(moduleStr);

      return parsed.map((m, i) => {
        const color = MODULE_BADGE_COLOR[m.type] || "primary";

        return (
          <span
            key={i}
            className={`badge bg-${color}-subtle text-${color} rounded-pill me-1 mb-1 fw-semibold text-nowrap`}
            style={{ fontSize: "0.7rem" }}
          >
            {m.type} · {inr(m.amount)}
          </span>
        );
      });
    } catch (e) {
      return <span className="text-danger small">Invalid data</span>;
    }
  };

  const getStudentInfo = (item) => {
    if (item?.student_id && item?.student) {
      return {
        name: item.student.name,
        registrationNo: item.student.registration_no,
        type: "Student",
        link: `/student/details?id=${item.student_id}`,
      };
    }

    if (item?.bus_student_id && item?.bus_student) {
      return {
        name: item.bus_student.student_name || item.bus_student.name || "-",
        registrationNo:
          item.bus_student.registration_no ||
          item.bus_student.registration_no ||
          null,
        type: "Bus Student",
        link: null,
      };
    }

    if (item?.library_student_id && item?.library_student) {
      return {
        name:
          item.library_student.student_name || item.library_student.name || "-",
        registrationNo: item.library_student.registration_no || null,
        type: "Library Student",
        link: null,
      };
    }

    if (item?.hostel_student_id && item?.hostel_student) {
      return {
        name:
          item.hostel_student.student_name || item.hostel_student.name || "-",
        registrationNo: item.hostel_student.registration_no || null,
        type: "Hostel Student",
        link: null,
      };
    }

    return {
      name: "-",
      registrationNo: null,
      type: null,
      link: null,
    };
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    {/* ================= HEADER / FILTERS ================= */}

                    <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        {/* PAGE TITLE */}
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
                            style={{
                              width: 46,
                              height: 46,
                            }}
                          >
                            <i className="ri-money-rupee-circle-line"></i>
                          </div>

                          <div>
                            <h5 className="m-0 fw-bold">Fees Transactions</h5>

                            <small className="text-muted">
                              Review and accept pending fee payment requests
                            </small>
                          </div>
                        </div>

                        {/* COMPLETE HISTORY BUTTON */}
                        <Link
                          to="/student/fees/transaction/history"
                          className="btn btn-outline-primary d-flex align-items-center gap-2"
                        >
                          <i className="ri-history-line"></i>
                          <span>Complete History</span>
                        </Link>
                      </div>
                    </div>

                    {/* ================= TRANSACTIONS TABLE ================= */}

                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
                      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white"
                            style={{
                              width: 44,
                              height: 44,
                              background:
                                "linear-gradient(135deg, #10b981, #22c55e)",
                            }}
                          >
                            <i className="ri-file-list-3-line fs-5"></i>
                          </div>

                          <div>
                            <h6 className="m-0 fw-bold">Transaction List</h6>

                            <small className="text-muted">
                              All fee receipts matching the selected filters
                            </small>
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                            <i className="ri-list-check me-1"></i>
                            {data?.length || 0} record
                            {data?.length === 1 ? "" : "s"}
                          </span>
                        </div>
                      </div>

                      {/* ================= TABLE ================= */}

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr className="text-uppercase small text-muted">
                              <th className="ps-4 py-3 fw-semibold">#</th>

                              <th className="py-3 fw-semibold">Student</th>

                              <th className="py-3 fw-semibold">Module</th>

                              <th className="py-3 fw-semibold text-end">
                                Amount
                              </th>

                              <th className="py-3 fw-semibold">Bank</th>

                              <th className="py-3 fw-semibold">Date</th>

                              <th className="py-3 fw-semibold">Img</th>

                              <th className="py-3 fw-semibold pe-4 text-end">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.length > 0 ? (
                              data.map((item, index) => (
                                <tr key={item.id}>
                                  {/* NUMBER */}

                                  <td className="ps-4">{index + 1}</td>

                                  {/* STUDENT */}

                                  <td>
                                    {(() => {
                                      const studentInfo = getStudentInfo(item);

                                      if (studentInfo.link) {
                                        return (
                                          <Link
                                            className="fw-semibold text-decoration-none"
                                            to={studentInfo.link}
                                          >
                                            {studentInfo.name}

                                            {studentInfo.registrationNo && (
                                              <span className="text-muted fw-normal">
                                                {" "}
                                                ({studentInfo.registrationNo})
                                              </span>
                                            )}
                                          </Link>
                                        );
                                      }

                                      return (
                                        <div>
                                          <span className="fw-semibold">
                                            {studentInfo.name}
                                          </span>

                                          {studentInfo.registrationNo && (
                                            <span className="text-muted fw-normal">
                                              {" "}
                                              ({studentInfo.registrationNo})
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </td>

                                  {/* MODULE */}

                                  <td>{parseModule(item.module)}</td>

                                  {/* AMOUNT */}

                                  <td className="fw-bold text-success text-end text-nowrap">
                                    {inr(item.paid ?? item.amount)}
                                  </td>

                                  {/* BANK */}

                                  <td>
                                    {item.bank ? item?.bank?.account_no : "-"}
                                  </td>

                                  {/* DATE */}

                                  <td>
                                    <span className="text-nowrap">
                                      {formatToDate(item.payment_date)}
                                    </span>
                                  </td>

                                  {/* IMAGE */}

                                  <td>
                                    {item?.image ? (
                                      <img
                                        src={imgBaseUrl + item.image}
                                        width="100px"
                                        height="70px"
                                        className="rounded-3"
                                        alt="Transaction"
                                        style={{
                                          cursor: "pointer",
                                          objectFit: "cover",
                                        }}
                                        onClick={() =>
                                          setSelectedImage(
                                            imgBaseUrl + item.image,
                                          )
                                        }
                                      />
                                    ) : (
                                      <span className="text-muted">
                                        No Image
                                      </span>
                                    )}
                                  </td>

                                  {/* ACTION */}

                                  <td className="pe-4">
                                    <div className="d-flex gap-2 justify-content-end">
                                      {hasAccess("/student/fees/receipt") && (
                                        <Link
                                          className="btn btn-label-primary btn-sm"
                                          to={`/student/fees/receipt?id=${item?.id}`}
                                          title="Print receipt"
                                        >
                                          <i className="ri-printer-line ri-20px"></i>
                                        </Link>
                                      )}

                                      <button
                                        type="button"
                                        className="btn btn-success btn-sm"
                                        onClick={() =>
                                          handleAccept(getAcceptPayload(item))
                                        }
                                      >
                                        Accept
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="8"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-inbox-line fs-2 d-block mb-2 text-secondary"></i>
                                  No Transactions Found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          FULL IMAGE POPUP
      ====================================================== */}

      {selectedImage && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 9999,
          }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-transparent border-0">
              <div className="modal-body text-center p-0 position-relative">
                {/* CLOSE BUTTON */}

                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                  style={{
                    zIndex: 10,
                  }}
                  onClick={() => setSelectedImage(null)}
                />

                {/* FULL IMAGE */}

                <img
                  src={selectedImage}
                  alt="Full Preview"
                  className="img-fluid rounded-3 shadow"
                  style={{
                    maxHeight: "90vh",
                    maxWidth: "95vw",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
